import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { User } from '@clerk/backend';
import { useAuth } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, set, addBusinessDays, formatDistanceToNow } from 'date-fns';
import {
  Calendar as CalendarIcon,
  UserPlus,
  Check,
  ListTodo,
  X,
  Search,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import * as z from 'zod';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAllClerkUsers } from '@/hooks/use-clerk-user';
import { useDeals } from '@/hooks/use-deals';
import type { Deal } from '@/hooks/use-deals';
import { useCreateTask } from '@/hooks/use-tasks';
import { cn } from '@/lib/utils';

// System deal ID used for tasks from FDIC/Monitor contexts when no deal is selected
const SYSTEM_DEAL_ID = 'test-deal-60b84a89';

const formSchema = z.object({
  task: z.string().min(1, 'Task description is required.'),
  due: z.date({
    required_error: 'A due date is required.',
  }),
  dueTime: z.object({
    hours: z.string(),
    minutes: z.string(),
    period: z.enum(['AM', 'PM']),
  }),
  assigned_to: z.array(z.string()).min(1, 'At least one assignee is required.'),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain: string;
  status: string;
  /** Optional context label (e.g., "FDIC" or "Monitor") for the deal_id prefix */
  context?: string;
}

function TimeSelect({ field }: {
  field: {
    value: { hours: string; minutes: string; period: 'AM' | 'PM' };
    onChange: (value: { hours: string; minutes: string; period: 'AM' | 'PM' }) => void;
  };
}) {
  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <div className="flex items-center gap-2">
      <Select
        value={field.value.hours}
        onValueChange={(value) => field.onChange({ ...field.value, hours: value })}
      >
        <SelectTrigger className="w-[70px]">
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent>
          {hours.map((hour) => (
            <SelectItem key={hour} value={hour}>
              {hour}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span>:</span>
      <Select
        value={field.value.minutes}
        onValueChange={(value) => field.onChange({ ...field.value, minutes: value })}
      >
        <SelectTrigger className="w-[70px]">
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent>
          {minutes.map((minute) => (
            <SelectItem key={minute} value={minute}>
              {minute}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={field.value.period}
        onValueChange={(value: 'AM' | 'PM') => field.onChange({ ...field.value, period: value })}
      >
        <SelectTrigger className="w-[70px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function formatStatusLabel(status: string): string {
  return status.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());
}

export function CreateTaskDialog({
  open,
  onOpenChange,
  domain,
  status,
  context = 'monitor',
}: CreateTaskDialogProps) {
  const { userId } = useAuth();
  const { data: users } = useAllClerkUsers();
  const { data: deals, isLoading: isLoadingDeals } = useDeals();

  // Quick user lookup for the assignee field (hoisted out of the render prop
  // so the hook isn't called inside a callback)
  const userMap = useMemo(() => {
    const map = new Map<string, Partial<User>>();
    if (users) {
      for (const user of users) {
        if (user?.id)
          map.set(user.id, user);
      }
    }
    return map;
  }, [users]);
  const { mutate: createTask, isPending: isCreatingTask } = useCreateTask();
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [dealSearchOpen, setDealSearchOpen] = useState(false);

  const defaultTaskText = `FU on ${formatStatusLabel(status)} for ${domain}`;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      task: defaultTaskText,
      assigned_to: userId ? [userId] : [],
      due: addBusinessDays(new Date(), 2),
      dueTime: {
        hours: '09',
        minutes: '00',
        period: 'AM' as const,
      },
    },
  });

  // Find deals that contain this domain, sorted by created_at (most recent first)
  const matchingDeals = useMemo(() => {
    if (!deals)
      return [];
    return deals
      .filter((deal) =>
        deal.domains.some((d) => d.toLowerCase().includes(domain.toLowerCase())),
      )
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [deals, domain]);

  // Reset form and deal selection when dialog opens with new domain/status
  useEffect(() => {
    if (open) {
      form.reset({
        task: `FU on ${formatStatusLabel(status)} for ${domain}`,
        assigned_to: userId ? [userId] : [],
        due: addBusinessDays(new Date(), 2),
        dueTime: {
          hours: '09',
          minutes: '00',
          period: 'AM' as const,
        },
      });
      // Auto-select if there's exactly one matching deal
      if (matchingDeals.length === 1) {
        setSelectedDeal(matchingDeals[0]);
      } else {
        setSelectedDeal(null);
      }
    }
  }, [open, domain, status, userId, form, matchingDeals]);

  // Helper to format domains list with matched domain first and bold
  function formatDomains(domains: string[], matchedDomain: string, maxLength = 50): React.ReactNode {
    // Find the matching domain and put it first
    const lowerMatched = matchedDomain.toLowerCase();
    const matchingDomains = domains.filter((d) => d.toLowerCase().includes(lowerMatched));
    const otherDomains = domains.filter((d) => !d.toLowerCase().includes(lowerMatched));
    const reordered = [...matchingDomains, ...otherDomains];

    // Build the display with the first (matched) domain bold
    if (reordered.length === 0)
      return '—';

    const firstDomain = reordered[0];
    const rest = reordered.slice(1);

    if (rest.length === 0) {
      return <span className="font-medium text-foreground">{firstDomain}</span>;
    }

    const restJoined = rest.join(', ');
    const totalLength = firstDomain.length + 2 + restJoined.length;

    if (totalLength <= maxLength) {
      return (
        <>
          <span className="font-medium text-foreground">{firstDomain}</span>
          <span>
            ,
            {restJoined}
          </span>
        </>
      );
    }

    // Truncate the rest
    const available = maxLength - firstDomain.length - 5; // 5 for ", ..."
    return (
      <>
        <span className="font-medium text-foreground">{firstDomain}</span>
        <span>
          ,
          {restJoined.slice(0, Math.max(0, available))}
          ...
        </span>
      </>
    );
  }

  function onSubmit(values: FormValues) {
    // Convert 12-hour time to 24-hour time
    let hours = Number.parseInt(values.dueTime.hours);
    if (values.dueTime.period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (values.dueTime.period === 'AM' && hours === 12) {
      hours = 0;
    }

    const dueDate = set(values.due, {
      hours,
      minutes: Number.parseInt(values.dueTime.minutes),
      seconds: 0,
      milliseconds: 0,
    });

    // Use selected deal ID or fall back to system deal
    const dealId = selectedDeal?.id ?? SYSTEM_DEAL_ID;

    createTask(
      {
        deal_id: dealId,
        task: values.task,
        due: dueDate.toISOString(),
        assigned_to: values.assigned_to,
      },
      {
        onSuccess: () => {
          toast.success('Task created');
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="size-5" />
            Create Task
          </DialogTitle>
          <DialogDescription>
            Create a follow-up task for
            {' '}
            <span className="font-medium text-foreground">{domain}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Deal Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Associated Deal</label>
          {selectedDeal
            ? (
                <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
                  <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {selectedDeal.deal_type}
                      </Badge>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        {selectedDeal.deal_stage}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {formatDomains(selectedDeal.domains, domain)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Created
                      {' '}
                      {formatDistanceToNow(new Date(selectedDeal.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6 shrink-0"
                    onClick={() => setSelectedDeal(null)}
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              )
            : (matchingDeals.length > 0
                ? (
                    <Popover open={dealSearchOpen} onOpenChange={setDealSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start gap-2 text-muted-foreground"
                        >
                          <Search className="size-4" />
                          {isLoadingDeals
                            ? 'Loading deals...'
                            : `${matchingDeals.length} deal${matchingDeals.length === 1 ? '' : 's'} found for ${domain}`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[420px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Filter deals..." />
                          <CommandEmpty>No matching deals.</CommandEmpty>
                          <CommandGroup className="max-h-[300px] overflow-y-auto">
                            {matchingDeals.map((deal) => (
                              <CommandItem
                                key={deal.id}
                                onSelect={() => {
                                  setSelectedDeal(deal);
                                  setDealSearchOpen(false);
                                }}
                                className="flex flex-col items-start gap-1.5 py-3"
                              >
                                <div className="flex w-full items-center gap-2">
                                  <Badge variant="outline" className="shrink-0 text-xs">
                                    {deal.deal_type}
                                  </Badge>
                                  <Badge variant="secondary" className="shrink-0 text-xs">
                                    {deal.deal_stage}
                                  </Badge>
                                  <span className="ml-auto text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(deal.created_at), { addSuffix: true })}
                                  </span>
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {formatDomains(deal.domains, domain, 55)}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )
                : (
                    <div className="rounded-md border border-dashed p-3 text-center text-sm text-muted-foreground">
                      {isLoadingDeals ? 'Loading deals...' : `No deals found containing "${domain}"`}
                    </div>
                  ))}
          {!selectedDeal && matchingDeals.length > 0 && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertCircle className="size-3" />
              Select a deal above, or task will be added to the system deal.
            </p>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="task"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Description</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="What needs to be done?"
                      rows={3}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="due"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start gap-2 text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          <CalendarIcon className="size-4" />
                          {field.value
                            ? format(
                                set(field.value, {
                                  hours: form.getValues('dueTime').period === 'PM' && form.getValues('dueTime').hours !== '12'
                                    ? Number(form.getValues('dueTime').hours) + 12
                                    : (form.getValues('dueTime').period === 'AM' && form.getValues('dueTime').hours === '12'
                                        ? 0
                                        : Number(form.getValues('dueTime').hours)),
                                  minutes: Number(form.getValues('dueTime').minutes),
                                }),
                                'MMM d, h:mm a',
                              )
                            : 'Select date'}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                      <div className="border-t p-3">
                        <FormField
                          control={form.control}
                          name="dueTime"
                          render={({ field }) => (
                            <div>
                              <FormLabel className="text-xs text-muted-foreground">Time</FormLabel>
                              <TimeSelect field={field} />
                            </div>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assigned_to"
              render={({ field }) => {
                const selectedUsers = field.value
                  .map((id) => userMap.get(id))
                  .filter((u): u is Partial<User> => !!u);

                return (
                  <FormItem>
                    <FormLabel>Assign To</FormLabel>
                    <div className="space-y-3">
                      {/* Selected users as removable badges */}
                      {selectedUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedUsers.map((user) => (
                            <Badge
                              key={user.id}
                              variant="secondary"
                              className="gap-1.5 py-1 pl-1 pr-2"
                            >
                              <Avatar className="size-5">
                                <AvatarImage src={user.imageUrl} />
                                <AvatarFallback className="text-[10px]">
                                  {user.firstName?.[0]}
                                  {user.lastName?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs font-medium">
                                {user.firstName}
                                {' '}
                                {user.lastName}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  field.onChange(field.value.filter((id) => id !== user.id));
                                }}
                                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                              >
                                <X className="size-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Add user popover */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <UserPlus className="size-4" />
                              {selectedUsers.length === 0 ? 'Add assignee' : 'Add another'}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-[320px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search users..." />
                            <CommandEmpty>No users found.</CommandEmpty>
                            <CommandGroup className="max-h-[250px] overflow-y-auto">
                              {users?.map((user) => {
                                if (!user)
                                  return null;
                                const isSelected = field.value.includes(user.id ?? '');
                                return (
                                  <CommandItem
                                    key={user.id}
                                    onSelect={() => {
                                      if (isSelected) {
                                        field.onChange(field.value.filter((id) => id !== user.id));
                                      } else {
                                        field.onChange([...field.value, user.id ?? '']);
                                      }
                                    }}
                                    className="gap-3"
                                  >
                                    <Avatar className="size-8">
                                      <AvatarImage src={user.imageUrl} />
                                      <AvatarFallback className="text-xs">
                                        {user.firstName?.[0]}
                                        {user.lastName?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-1 flex-col gap-0.5">
                                      <span className="text-sm font-medium">
                                        {user.firstName}
                                        {' '}
                                        {user.lastName}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {user.primaryEmailAddress?.emailAddress}
                                      </span>
                                    </div>
                                    <div
                                      className={cn(
                                        'flex size-5 items-center justify-center rounded-full border-2 transition-colors',
                                        isSelected
                                          ? 'border-primary bg-primary text-primary-foreground'
                                          : 'border-muted',
                                      )}
                                    >
                                      {isSelected && <Check className="size-3" />}
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </FormItem>
                );
              }}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreatingTask}
              >
                {isCreatingTask ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
